import { useCallback, useMemo, useState } from "react";
import { cloneDeep, isEqual } from "lodash";
import { v4 as uuidv4 } from "uuid";
import {
  ADD_ATTRIBUTE_POPUP,
  EDIT_ATTRIBUTE_POPUP,
  TOGGLE_POSITIONED_POPUP,
} from "../../../../constants";
import { useAppState } from "../../../../state/appState";
import {
  useCreateAttributes,
  useDeleteAttributes,
  useGetAttributes,
  usePatchAttributes,
} from "../../../../hooks/api/attributes";
import formatAttributesPayload from "../../../../helpers/Attribute/formatAttributePayload";
import { toastError, toastMessage } from "../../Toast/Toast";

const useAttributesManagementList = () => {
  const [, appStateDispatch] = useAppState();
  const [isEditing, setIsEditing] = useState(false);
  const [savingAttributes, setSavingAttributes] = useState(false);

  const { attributes: originalAttributes, attributesLoading } =
    useGetAttributes();

  const [attributes, setAttributes] = useState([]);

  const { createAttributes } = useCreateAttributes();
  const { editAttributes } = usePatchAttributes();
  const { deleteAttributes } = useDeleteAttributes();

  // check form for changes to enable/disable edit button
  const changesMade = useMemo(() => {
    const wereChangesMade = isEqual(originalAttributes, attributes);

    return wereChangesMade;
  }, [attributes, originalAttributes]);

  const onCancel = () => {
    setIsEditing(false);
    const cloned = cloneDeep(originalAttributes);
    setAttributes(cloned);
  };

  const handleEditClick = () => {
    setIsEditing(true);
    const clone = cloneDeep(originalAttributes);
    setAttributes(clone);
  };

  const handleCheckbox = useCallback(
    ({ value, type, id }) => {
      const copy = [...attributes];
      const indexToUpdate = copy.findIndex((item) => item.id === id);
      copy[indexToUpdate] = {
        ...copy[indexToUpdate],
        wasUpdated: true,
        applyTo: {
          ...copy[indexToUpdate].applyTo,
          [type]: value,
        },
      };
      setAttributes([...copy]);
    },
    [attributes]
  );

  const handleRemoveAttribute = useCallback(
    ({ id }) => {
      const updatedList = attributes?.filter((item) => item.id !== id);
      if (updatedList?.length > 0) {
        setAttributes([...updatedList]);
      } else {
        setAttributes([]);
      }
    },
    [attributes, setAttributes]
  );

  // Submit Function for Edit Single Attribute Popup
  const onFinish = useCallback(
    ({ attribute }) => {
      // update attributes
      const indexToUpdate = attributes?.findIndex(
        (item) => item.id === attribute?.id
      );
      const copy = [...attributes];
      if (indexToUpdate) {
        const updated = { ...attribute, wasUpdated: true };
        copy[indexToUpdate] = updated;
        setAttributes([...copy]);
      }
    },
    [attributes, setAttributes]
  );

  const submitNewAttribute = useCallback((newAttribute) => {
    const formattedNewAttribute = {
      ...newAttribute,
      wasAdded: true,
      id: uuidv4(),
    };
    setAttributes((prev) => [...prev, formattedNewAttribute]);
  }, []);

  const handleAddAttribute = () => {
    appStateDispatch({
      type: TOGGLE_POSITIONED_POPUP,
      position: {
        x:
          window.innerWidth < 1200
            ? window.innerWidth / 5
            : window.innerWidth / 2.5,
        y: window.innerHeight / 5,
      },
      popupData: {
        item: {
          onSubmit: submitNewAttribute,
          isAdminPage: true,
        },
      },
      popupType: ADD_ATTRIBUTE_POPUP,
    });
  };

  // CREATE NEW EDIT ATTRIBUTE POPUP
  const handleEditAttribute = ({ attribute }) => {
    appStateDispatch({
      type: TOGGLE_POSITIONED_POPUP,
      position: {
        x:
          window.innerWidth < 1200
            ? window.innerWidth / 5
            : window.innerWidth / 2.5,
        y: window.innerHeight / 5,
      },
      popupData: {
        item: {
          attribute,
          onFinish,
        },
      },
      popupType: EDIT_ATTRIBUTE_POPUP,
    });
  };

  const onSave = useCallback(async () => {
    const { createdItems, editedItems, deletedItemsIds } =
      await formatAttributesPayload(attributes, originalAttributes);

    setSavingAttributes(true);
    try {
      if (createdItems?.length > 0) {
        const props = {
          args: {
            createdItems,
          },
        };
        await createAttributes(props);
      }
      if (editedItems?.length > 0) {
        const props = {
          args: { editedItems },
        };

        await editAttributes(props);
      }
      if (deletedItemsIds?.length > 0) {
        const props = {
          args: { deletedItemsIds },
        };
        await deleteAttributes(props);
      }
      setIsEditing(false);
      setAttributes([]);
      toastMessage("Attributes Updated.");
    } catch (e) {
      toastError("Error updating attributes.");
      console.error("Error onUpdateAttributes.", e);
    } finally {
      setSavingAttributes(false);
    }
  }, [
    attributes,
    createAttributes,
    deleteAttributes,
    editAttributes,
    originalAttributes,
  ]);

  return {
    isEditing,
    setIsEditing,
    handleCheckbox,
    handleEditAttribute,
    handleAddAttribute,
    handleRemoveAttribute,
    attributes,
    originalAttributes,
    handleEditClick,
    onSave,
    onCancel,
    changesMade,
    attributesLoading,
    savingAttributes,
  };
};

export default useAttributesManagementList;
